import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  getClubs, 
  createClub, 
  updateClub, 
  deleteClub,
  api 
} from '../../services/api';

// Async thunks with improved error handling
export const fetchClubs = createAsyncThunk(
  'clubs/fetchClubs',
  async (_, { rejectWithValue }) => {
    try {
      const response = await getClubs();
      if (!response?.data) {
        throw new Error('Invalid response format');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to fetch clubs');
    }
  }
);

export const addClub = createAsyncThunk(
  'clubs/addClub',
  async (clubData, { rejectWithValue }) => {
    try {
      const response = await createClub(clubData);
      if (!response?.data) {
        throw new Error('Invalid response format');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to create club');
    }
  }
);

export const editClub = createAsyncThunk(
  'clubs/editClub',
  async ({ id, clubData }, { rejectWithValue }) => {
    try {
      const response = await updateClub(id, clubData);
      if (!response?.data) {
        throw new Error('Invalid response format');
      }
      return response.data;
    } catch (error) {
      // Handle specific error cases
      if (error.response?.status === 401) {
        return rejectWithValue('Current password is incorrect');
      }
      if (error.response?.status === 400) {
        return rejectWithValue(error.response.data.message || 'Invalid club data');
      }
      if (error.response?.status === 404) {
        return rejectWithValue('Club not found');
      }
      if (error.response?.status === 409) {
        return rejectWithValue('Email already exists');
      }
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to update club');
    }
  }
);

export const removeClub = createAsyncThunk(
  'clubs/removeClub',
  async (id, { rejectWithValue }) => {
    try {
      await deleteClub(id);
      return id;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to delete club');
    }
  }
);

export const rejectClub = createAsyncThunk(
  'clubs/rejectClub',
  async (id, { rejectWithValue }) => {
    try {
      const response = await api.post(`/clubs/${id}/reject`);
      if (!response?.data) {
        throw new Error('Invalid response format');
      }
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || 'Failed to reject club');
    }
  }
);

const initialState = {
  clubs: [],
  status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
  error: null,
  selectedClub: null,
  lastUpdated: null,
};

const clubSlice = createSlice({
  name: 'clubs',
  initialState,
  reducers: {
    setSelectedClub: (state, action) => {
      state.selectedClub = action.payload;
    },
    clearSelectedClub: (state) => {
      state.selectedClub = null;
    },
    clearError: (state) => {
      state.error = null;
    },
    resetStatus: (state) => {
      state.status = 'idle';
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch clubs
      .addCase(fetchClubs.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(fetchClubs.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.clubs = Array.isArray(action.payload) ? action.payload : [];
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchClubs.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
        state.clubs = [];
      })
      // Add club
      .addCase(addClub.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(addClub.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (Array.isArray(state.clubs)) {
          state.clubs.push(action.payload);
        } else {
          state.clubs = [action.payload];
        }
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(addClub.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Edit club
      .addCase(editClub.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(editClub.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (Array.isArray(state.clubs)) {
          const index = state.clubs.findIndex(club => club._id === action.payload._id);
          if (index !== -1) {
            state.clubs[index] = action.payload;
          }
        }
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(editClub.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Remove club
      .addCase(removeClub.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(removeClub.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (Array.isArray(state.clubs)) {
          state.clubs = state.clubs.filter(club => club._id !== action.payload);
        }
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(removeClub.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      })
      // Reject club
      .addCase(rejectClub.pending, (state) => {
        state.status = 'loading';
        state.error = null;
      })
      .addCase(rejectClub.fulfilled, (state, action) => {
        state.status = 'succeeded';
        if (Array.isArray(state.clubs)) {
          const index = state.clubs.findIndex(club => club._id === action.payload._id);
          if (index !== -1) {
            state.clubs[index] = action.payload;
          }
        }
        state.error = null;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(rejectClub.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload;
      });
  },
});

export const { 
  setSelectedClub, 
  clearSelectedClub, 
  clearError, 
  resetStatus 
} = clubSlice.actions;

// Selectors
export const selectAllClubs = (state) => state.clubs.clubs;
export const selectClubStatus = (state) => state.clubs.status;
export const selectClubError = (state) => state.clubs.error;
export const selectSelectedClub = (state) => state.clubs.selectedClub;
export const selectLastUpdated = (state) => state.clubs.lastUpdated;

export default clubSlice.reducer; 